import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} $`;

// Accueil : progression des encaissements du mois vs objectif (auto = contrats actifs ramenés au mois + 10 %, modifiable).
export function MonthlyGoalCard() {
  const [g, setG] = useState(null);
  const [q, setQ] = useState(null);
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState("");
  const load = () => {
    api.get("/ops/monthly-goal").then((r) => setG(r.data)).catch(() => setG({ error: true }));
    api.get("/ops/quarterly-goal").then((r) => setQ(r.data)).catch(() => setQ(null));
  };
  useEffect(() => { load(); }, []);
  if (!g || g.error) return null;
  const pct = Math.min(100, g.pct || 0);
  const color = g.pct >= 100 ? "bg-emerald-400" : g.on_track ? "bg-[#C9A961]" : "bg-rose-400";
  const save = async (amount) => {
    try { await api.put("/ops/monthly-goal", { month: g.month, amount }); toast.success(amount ? "Objectif enregistré" : "Objectif automatique rétabli"); setEdit(false); load(); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="mx-6 md:mx-8 lg:mx-10 mt-4 rounded-xl border border-[#C9A961]/40 bg-[#C9A961]/[0.05] p-4" data-testid="monthly-goal-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961] font-semibold">Objectif du mois · {g.label}</p>
          <h3 className="text-zinc-100 font-bold mt-0.5" data-testid="monthly-goal-title">{money(g.collected)} encaissés sur {money(g.goal)} <span className="text-zinc-400 font-normal text-sm">({g.pct} %)</span></h3>
          <p className="text-xs text-zinc-400 mt-0.5" data-testid="monthly-goal-hint">
            Jour {g.days_done}/{g.days_total} · attendu {g.expected_pct} % · {g.on_track ? "✅ en bonne voie" : "⚠️ en retard sur le rythme"} · reste {money(g.remaining)}
            {Object.keys(g.by_source || {}).length > 0 && <> · {Object.entries(g.by_source).map(([s, v]) => `${s} ${money(v)}`).join(" · ")}</>}
          </p>
        </div>
        <div className="text-right text-xs">
          {edit ? (
            <div className="flex items-center gap-1">
              <input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder={String(g.goal)} className="w-28 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-100" data-testid="monthly-goal-input" />
              <button onClick={() => save(parseFloat(val))} className="px-2 py-1 rounded bg-[#C9A961] text-black font-semibold" data-testid="monthly-goal-save">OK</button>
              <button onClick={() => save(null)} className="px-2 py-1 rounded bg-zinc-800 text-zinc-300" data-testid="monthly-goal-auto">Auto</button>
              <button onClick={() => setEdit(false)} className="px-2 py-1 text-zinc-500">✕</button>
            </div>
          ) : (
            <button onClick={() => { setVal(String(g.goal)); setEdit(true); }} className="text-[#C9A961] underline" data-testid="monthly-goal-edit">Objectif : {g.goal_source}</button>
          )}
          <p className="text-zinc-500 mt-1">Base : {g.auto.contracts} contrats actifs · {money(g.auto.recurring_28d)}/28 j → {money(g.auto.recurring_monthly)}/mois</p>
        </div>
      </div>
      <div className="mt-3 h-3 rounded-full bg-zinc-800 overflow-hidden relative" data-testid="monthly-goal-bar">
        <div className={`h-full ${color} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
        <div className="absolute top-0 bottom-0 w-px bg-zinc-300/60" style={{ left: `${Math.min(100, g.expected_pct)}%` }} title="Rythme attendu" />
      </div>
      {q && (
        <div className="mt-4 rounded-lg border border-zinc-800 bg-black/30 p-3" data-testid="quarterly-goal-block">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-semibold">Trimestre glissant · {q.label}</p>
            <p className="text-xs text-zinc-300" data-testid="quarterly-goal-title"><b className="text-zinc-100">{money(q.collected)}</b> / {money(q.goal)} · {q.pct} % <span className="text-zinc-500">(attendu {q.expected_pct} %)</span> · {q.on_track ? "✅" : "⚠️"}</p>
          </div>
          <div className="mt-2 h-2 rounded-full bg-zinc-800 overflow-hidden relative">
            <div className={`h-full ${q.pct >= 100 ? "bg-emerald-400" : q.on_track ? "bg-[#C9A961]" : "bg-rose-400"}`} style={{ width: `${Math.min(100, q.pct)}%` }} />
            <div className="absolute top-0 bottom-0 w-px bg-zinc-300/60" style={{ left: `${Math.min(100, q.expected_pct)}%` }} />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
            {q.months.map((m) => (
              <div key={m.month} className="text-zinc-400" data-testid={`quarter-month-${m.month}`}>
                <span className="text-zinc-300 capitalize">{m.label.split(" ")[0]}</span> · {money(m.collected)} <span className="text-zinc-500">({m.pct} %)</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">{q.cycles_note}</p>
        </div>
      )}
      {g.disciplines?.length > 0 && (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2" data-testid="monthly-goal-disciplines">
          {g.disciplines.map((d) => {
            const p = d.goal ? Math.min(100, (100 * d.collected) / d.goal) : 0;
            const c = d.pct === null ? "bg-zinc-500" : d.pct >= 100 ? "bg-emerald-400" : d.pct >= g.expected_pct - 5 ? "bg-[#C9A961]" : "bg-rose-400";
            return (
              <div key={d.label} data-testid={`goal-disc-${d.label.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}>
                <div className="flex justify-between text-[11px]">
                  <span className="text-zinc-300 font-medium truncate">{d.label}{d.contracts ? <span className="text-zinc-500"> · {d.contracts}</span> : null}</span>
                  <span className="text-zinc-400 whitespace-nowrap ml-2">{money(d.collected)}{d.goal ? ` / ${money(d.goal)} · ${d.pct} %` : ""}</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden"><div className={`h-full ${c}`} style={{ width: `${d.goal ? p : 100}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
